/**
 * useTreatmentDashboard
 *
 * Bridges real cycle data to the TreatmentDashboard hero on the home
 * page. Returns cycle-level state (isInTreatment, current phase, day in
 * phase, progress) and the monitoring, retrieval and beta readings the
 * hero shows.
 *
 * Today's meds + appointments are NOT built here. The home page renders
 * them through the concept `TodayList` adapter used by the redesigned Care
 * calendar, so we don't keep parallel agenda pipelines.
 */

import { useMemo } from "react";
import { useActiveCycleWithPhases } from "@src/packages/cycles/hooks/useCycles";
import type {
  CycleType as BackendCycleType,
  Phase,
  PhaseType,
} from "@src/packages/cycles/hooks/useCycles";
import { isRetrievalStillAhead } from "@src/packages/cycles/presenters/retrievalStillAhead";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { getTodayString } from "@src/packages/utils/date";

export type TreatmentPhase =
  | "baseline"
  | "stimulation"
  | "trigger"
  | "retrieval"
  | "fertilization"
  | "embryo_development"
  | "transfer"
  | "waiting"
  | "complete";

// Map backend phase types to our TreatmentPhase
const PHASE_MAP: Record<string, TreatmentPhase> = {
  pre_treatment: "baseline",
  protocol: "stimulation",
  egg_retrieval: "retrieval",
  fertilization: "fertilization",
  embryo_development: "embryo_development",
  transfer: "transfer",
  luteal_phase: "waiting",
  sperm_collection: "retrieval",
  insemination: "fertilization",
};

function getCycleTypeLabel(
  cycleType: string | null | undefined,
  t: TranslationFunction,
): string {
  if (!cycleType) return t("treatment_dashboard.cycle_fallback");
  switch (cycleType) {
    case "ivf":
      return t("care.start_treatment.cycle_ivf");
    case "iui":
      return t("care.start_treatment.cycle_iui");
    case "fet":
      return t("care.start_treatment.cycle_fet");
    case "icsi":
      return t("care.start_treatment.cycle_icsi");
    case "oi":
      return t("care.start_treatment.cycle_oi");
    case "egg_freezing":
      return t("care.start_treatment.cycle_egg_freezing");
    case "embryo_freezing":
      return t("care.start_treatment.cycle_embryo_freezing");
    case "sperm_freezing":
      return t("care.start_treatment.cycle_sperm_freezing");
    default:
      return t("treatment_dashboard.cycle_fallback");
  }
}

function daysBetween(start: string, end: string): number {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return Math.floor(
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
  );
}

export type FollicleBand = {
  key: "small" | "growing" | "near-ready" | "ready" | "unknown";
  label: string;
  range: string;
  count: number;
};

export type MonitoringSnapshot = {
  date: string;
  totalFollicles: number;
  follicleSizes: number[];
  unknownFollicleCount: number;
  leadFollicleMm?: number;
  leftCount?: number;
  rightCount?: number;
  estradiol?: {
    value: number;
    unit: string;
  };
  endometriumMm?: number;
  bands: FollicleBand[];
};

export type MonitoringEvent = {
  label: string;
  detail?: string;
};

export type RetrievalEstimate = {
  date: string;
  isEstimate: boolean;
};

export type UseTreatmentDashboardResult = {
  isInTreatment: boolean;
  isLoading: boolean;
  cycleId: string | undefined;
  rawCycleType: BackendCycleType | undefined;
  cycleType: string;
  rawCurrentPhase: PhaseType | undefined;
  /**
   * The egg retrieval is booked for today or later and nothing records it yet,
   * so the retrieval phase being current does not mean it has happened.
   */
  retrievalStillAhead: boolean;
  currentPhase: TreatmentPhase;
  dayInPhase: number;
  totalPhaseDays: number | undefined;
  overallProgress: number;
  monitoringSnapshot: MonitoringSnapshot | null;
  nextMonitoringHint: MonitoringEvent | null;
  estimatedRetrieval: RetrievalEstimate | null;
  /** Scheduled beta blood-test date (the luteal-phase endDate the user/clinic
   *  recorded). `null` until the test is on the calendar — callers should
   *  fall back to no-date copy rather than fabricating one. */
  estimatedBetaDate: string | null;
};

function numericValue(
  value: string | number | null | undefined,
): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (!value) return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function addDays(date: string, days: number): string {
  // All arithmetic in UTC: parsing as local midnight but serializing via
  // toISOString() (UTC) shifts the result a day off in UTC+ timezones.
  const target = new Date(`${date}T00:00:00Z`);
  target.setUTCDate(target.getUTCDate() + days);
  return target.toISOString().split("T")[0]!;
}

function buildBands(
  sizes: number[],
  unknownCount: number,
  t: TranslationFunction,
): FollicleBand[] {
  const bands: FollicleBand[] = [
    {
      key: "small",
      label: t("treatment_dashboard.follicle_bands.small_label"),
      range: t("treatment_dashboard.follicle_bands.small_range"),
      count: 0,
    },
    {
      key: "growing",
      label: t("treatment_dashboard.follicle_bands.growing_label"),
      range: t("treatment_dashboard.follicle_bands.growing_range"),
      count: 0,
    },
    {
      key: "near-ready",
      label: t("treatment_dashboard.follicle_bands.near_ready_label"),
      range: t("treatment_dashboard.follicle_bands.near_ready_range"),
      count: 0,
    },
    {
      key: "ready",
      label: t("treatment_dashboard.follicle_bands.ready_label"),
      range: t("treatment_dashboard.follicle_bands.ready_range"),
      count: 0,
    },
  ];

  for (const size of sizes) {
    if (size < 10) bands[0]!.count += 1;
    else if (size < 14) bands[1]!.count += 1;
    else if (size < 18) bands[2]!.count += 1;
    else bands[3]!.count += 1;
  }

  if (unknownCount > 0) {
    bands.push({
      key: "unknown",
      label: t("treatment_dashboard.follicle_bands.unknown_label"),
      range: t("treatment_dashboard.follicle_bands.unknown_range"),
      count: unknownCount,
    });
  }

  return bands;
}

function buildMonitoringSnapshot(
  phases: Phase[],
  t: TranslationFunction,
): MonitoringSnapshot | null {
  const follicles = phases.flatMap((phase) => phase.ovaryFollicles ?? []);
  const datedFollicles = follicles.filter((f) => f.date);
  if (datedFollicles.length === 0) return null;

  const latestDate = datedFollicles
    .map((f) => f.date!)
    .sort((a, b) => b.localeCompare(a))[0]!;
  const latestFollicles = datedFollicles.filter((f) => f.date === latestDate);
  const follicleSizes: number[] = [];
  let unknownFollicleCount = 0;
  let leftCount = 0;
  let rightCount = 0;

  for (const follicle of latestFollicles) {
    const count = Math.max(1, Math.round(Number(follicle.count ?? 1)));
    if (follicle.ovarySide === "left") leftCount += count;
    if (follicle.ovarySide === "right") rightCount += count;

    const size = numericValue(follicle.size);
    if (size !== null) {
      for (let i = 0; i < count; i += 1) follicleSizes.push(size);
    } else {
      unknownFollicleCount += count;
    }
  }

  const totalFollicles = follicleSizes.length + unknownFollicleCount;
  if (totalFollicles === 0) return null;

  const bloodResults = phases
    .flatMap((phase) => phase.testResults ?? [])
    .filter((test) => test.date && test.type === "blood")
    .sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
  const estradiolResult =
    bloodResults.find((test) => {
      const name = (test.name ?? "").toLowerCase();
      return (
        test.date === latestDate &&
        (name.includes("estradiol") || name === "e2")
      );
    }) ??
    bloodResults.find((test) => {
      const name = (test.name ?? "").toLowerCase();
      return name.includes("estradiol") || name === "e2";
    });
  const estradiolValue = estradiolResult
    ? numericValue(
        estradiolResult.doctorValue ??
          estradiolResult.patientValue ??
          estradiolResult.value,
      )
    : null;

  const endometriumMetric = phases
    .flatMap((phase) => phase.fertilityMetrics ?? [])
    .filter((metric) => metric.type === "endometrial_thickness")
    .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""))[0];
  const endometriumMm = endometriumMetric
    ? numericValue(endometriumMetric.value)
    : null;

  return {
    date: latestDate,
    totalFollicles,
    follicleSizes: follicleSizes.sort((a, b) => a - b),
    unknownFollicleCount,
    leadFollicleMm:
      follicleSizes.length > 0 ? Math.max(...follicleSizes) : undefined,
    leftCount: leftCount > 0 ? leftCount : undefined,
    rightCount: rightCount > 0 ? rightCount : undefined,
    estradiol:
      estradiolValue !== null
        ? {
            value: estradiolValue,
            unit: estradiolResult?.unit ?? "pg/mL",
          }
        : undefined,
    endometriumMm: endometriumMm ?? undefined,
    bands: buildBands(follicleSizes, unknownFollicleCount, t),
  };
}

export function useTreatmentDashboard(): UseTreatmentDashboardResult {
  const t = useTranslations();
  const {
    cycle,
    phases,
    currentPhase,
    progress,
    isLoading: cycleLoading,
  } = useActiveCycleWithPhases();

  const isInTreatment = !!cycle && cycle.status === "open";

  const derivedPhase: TreatmentPhase = useMemo(() => {
    if (!currentPhase) return "baseline";
    return PHASE_MAP[currentPhase.type] ?? "stimulation";
  }, [currentPhase]);

  const dayInPhase = useMemo(() => {
    if (!currentPhase?.startDate) return 1;
    return Math.max(
      1,
      daysBetween(currentPhase.startDate, getTodayString()) + 1,
    );
  }, [currentPhase]);

  const totalPhaseDays = useMemo(() => {
    if (derivedPhase === "stimulation") return 12;
    if (derivedPhase === "baseline") return 3;
    if (derivedPhase === "waiting") return 14;
    if (derivedPhase === "embryo_development") return 6;
    return undefined;
  }, [derivedPhase]);

  const cycleTypeLabel = getCycleTypeLabel(cycle?.cycleType, t);

  const monitoringSnapshot = useMemo(
    () => buildMonitoringSnapshot(phases, t),
    [phases, t],
  );
  const nextMonitoringHint = useMemo<MonitoringEvent | null>(() => {
    if (derivedPhase !== "stimulation") return null;
    return {
      label: t("treatment_dashboard.monitoring_hint.label"),
      detail: t("treatment_dashboard.monitoring_hint.detail"),
    };
  }, [derivedPhase, t]);
  const estimatedRetrieval = useMemo<RetrievalEstimate | null>(() => {
    const retrievalPhase = phases.find(
      (phase) => phase.type === "egg_retrieval",
    );
    if (retrievalPhase?.startDate) {
      return { date: retrievalPhase.startDate, isEstimate: false };
    }
    if (
      derivedPhase !== "stimulation" ||
      !currentPhase?.startDate ||
      !totalPhaseDays
    ) {
      return null;
    }
    return {
      date: addDays(currentPhase.startDate, totalPhaseDays),
      isEstimate: true,
    };
  }, [currentPhase?.startDate, derivedPhase, phases, totalPhaseDays]);

  const estimatedBetaDate = useMemo<string | null>(() => {
    const luteal = phases.find((phase) => phase.type === "luteal_phase");
    return luteal?.endDate ?? null;
  }, [phases]);

  const retrievalStillAhead = isRetrievalStillAhead(
    phases.find((phase) => phase.type === "egg_retrieval"),
    getTodayString(),
  );

  return {
    isInTreatment,
    isLoading: cycleLoading,
    cycleId: cycle?.id,
    rawCycleType: cycle?.cycleType,
    cycleType: cycleTypeLabel,
    rawCurrentPhase: currentPhase?.type,
    retrievalStillAhead,
    currentPhase: derivedPhase,
    dayInPhase,
    totalPhaseDays,
    overallProgress: Math.round(progress ?? 0),
    monitoringSnapshot,
    nextMonitoringHint,
    estimatedRetrieval,
    estimatedBetaDate,
  };
}
